<template>
  <Aggregate
    title="Generators"
    v-model="computedValue"
    :ItemConstructor="generatorProjectFactory"
    :headers="aggregateHeaders"
    v-bind="$attrs"
    :cardProps="{ flat: true, class: [] }"
    toolbar-dense
    @change="$emit('change')"
  >
    <template #[`item.active`]="{ item }">
      <v-switch v-model="item.active" dense @change="$emit('change')"></v-switch>
    </template>
    <template #[`item.type`]="{ item }">
      <v-select
        v-model="item.type"
        :label="schema.type.title"
        :items="schema.type.enum"
        single-line
        @change="changeItemType(item)"
      >
        <template #selection="data"> {{ data.item | titleCase }}</template>
        <template #item="data"> {{ data.item | titleCase }} </template>
      </v-select>
    </template>
    <template #[`item.source`]="{ item }">
      <v-select
        v-if="item.type === 'RENEWABLE'"
        v-model="item.source"
        :label="schema.source.title"
        :items="renewableSourceEnum"
        single-line
        @change="$emit('change')"
      >
        <template #selection="data"> {{ data.item | titleCase }}</template>
        <template #item="data"> {{ data.item | titleCase }} </template>
      </v-select>
      <v-select
        v-if="item.type === 'THERMAL'"
        v-model="item.source"
        :label="schema.source.title"
        :items="thermalSourceEnum"
        single-line
        @change="$emit('change')"
      >
        <template #selection="data"> {{ data.item | titleCase }}</template>
        <template #item="data"> {{ data.item | titleCase }} </template>
      </v-select>
    </template>
    <template #[`item.capacity`]="{ item }">
      {{ `${item.capacity[0]} ${schema.capacity.unit}` }}
    </template>
    <template #default="{ item }">
      <v-expand-transition>
        <v-container class="pa-4 cols">
          <InputGeo
            v-model="item.coordinates"
            label="Coordinates"
            map-height="150"
            nullable
            switch-text="Use Generator coordinates"
            @change="$emit('change')"
          />
          <v-text-field
            v-model.number="item.startYear"
            :suffix="schema.start_year.unit"
            :label="schema.start_year.title"
            :hint="schema.start_year.tooltip"
            type="number"
            @change="$emit('change')"
          ></v-text-field>
          <InputYearlyProfile
            v-model="item.generationProfile"
            :label="schema.generation_profile.title"
            :suffix="schema.generation_profile.unit"
            :num_years="num_years"
            :profile-type="schema.generation_profile.profileType"
            :multi-year-upload="true"
            object-value
            :nullable="item.type === 'THERMAL'"
            @change="$emit('change')"
          ></InputYearlyProfile>
          <InputYearly
            v-model="item.capacity"
            :label="schema.capacity.title"
            :suffix="schema.capacity.unit"
            type="number"
            :num_years="num_years"
            :year_start="year_start"
            spread-values
            @change="$emit('change')"
          ></InputYearly>
          <InputToggleParam
            v-model="item.operating_status"
            :label="schema.operating_status.properties.active.title"
            :hint="schema.operating_status.properties.active.tooltip"
            :error-messages="$utils.getIfExists(errors, ['operating_status'])"
            @change="$emit('change')"
          >
            <template v-slot="{ param }">
              <InputProfile
                v-model="param.id"
                hint="Sets generator run mode (e.g., must-run, seasonal)"
                profile-type="DAILY_OPERATING_STATUS"
                hide-divider
                @change="$emit('change')"
              ></InputProfile>
            </template>
          </InputToggleParam>
          <v-row v-if="item.type === 'THERMAL'">
            <v-col cols="12" sm="4">
              <v-switch
                v-bind="$attrs"
                v-model="item.minimum_operating_load.active"
                :label="schema.minimum_operating_load.title"
                :hint="schema.minimum_operating_load.tooltip"
                persistent-hint
                @change="$emit('change')"
              ></v-switch>
            </v-col>
            <v-expand-transition>
              <v-col v-if="item.minimum_operating_load.active" cols="12" sm="8">
                <InputYearly
                  v-model="item.minimum_operating_load.value"
                  :hint="schema.minimum_operating_load.description"
                  :suffix="schema.minimum_operating_load.unit"
                  :error-messages="$utils.getIfExists(errors, ['value'])"
                  :num_years="num_years"
                  fill
                  hide-divider
                  :fill-value="1"
                  @change="$emit('change')"
                ></InputYearly>
              </v-col>
            </v-expand-transition>
          </v-row>
          <InputYearly
            v-if="item.type === 'RENEWABLE'"
            v-model="item.curtailmentPenaltyPrice"
            :label="schema.curtailment_penalty_price.title"
            :suffix="schema.curtailment_penalty_price.unit"
            type="number"
            :num_years="num_years"
            :year_start="year_start"
            @change="$emit('change')"
          ></InputYearly>
          <InputYearly
            v-model="item.vom"
            :label="schema.vom.title"
            hint="This parameter defines the variable costs for the unit"
            :suffix="schema.vom.unit"
            type="number"
            :num_years="num_years"
            :year_start="year_start"
            @change="$emit('change')"
          ></InputYearly>

          <v-expansion-panels class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header> Transient Stability Params </v-expansion-panel-header>
              <v-expansion-panel-content>
                <InputTextField
                  v-model="item.transient_ramp_rate"
                  label="Transient Ramp Rate"
                  tooltip="Maximum rate of power change during short-term system transitions"
                  suffix="MW/s"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>

                <InputYearly
                  v-model="item.inertia"
                  label="Inertia"
                  hint="Used for calculating inertia of system"
                  suffix="s"
                  type="number"
                  :num_years="num_years"
                  :year_start="year_start"
                  fill
                  :fill-value="0"
                  @change="$emit('change')"
                ></InputYearly>

                <InputYearly
                  v-model="item.droop"
                  label="Droop"
                  hint="Used for calculating droop of system"
                  suffix="%"
                  type="number"
                  :num_years="num_years"
                  :year_start="year_start"
                  fill
                  :fill-value="5"
                  @change="$emit('change')"
                ></InputYearly>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- PPAs -->
          <v-switch
            v-model="item.ppa.active"
            :label="schema.ppa.title"
            :hint="schema.ppa.tooltip"
            persistent-hint
            dense
            @change="$emit('change')"
          ></v-switch>
          <InputYearlyProfile
            v-if="item.ppa.active"
            v-model="item.ppa.energy"
            object-value
            fill
            toggle-items-type
            :label="schema.ppa.properties.energy.title"
            :hint="schema.ppa.properties.energy.tooltip"
            suffix="$/MWh"
            :num_years="num_years"
            :profile-type="schema.ppa.properties.energy.profileType"
            @change="$emit('change')"
          ></InputYearlyProfile>
          <InputYearlyProfile
            v-if="item.ppa.active"
            v-model="item.ppa.deemedEnergy"
            object-value
            fill
            toggle-items-type
            :label="schema.ppa.properties.deemedEnergy.title"
            :hint="schema.ppa.properties.deemedEnergy.tooltip"
            suffix="$/MWh"
            :num_years="num_years"
            :profile-type="schema.ppa.properties.deemedEnergy.profileType"
            @change="$emit('change')"
          ></InputYearlyProfile>
          <InputYearlyProfile
            v-if="item.ppa.active"
            v-model="item.ppa.capacity"
            object-value
            fill
            toggle-items-type
            :label="schema.ppa.properties.capacity.title"
            :hint="schema.ppa.properties.capacity.tooltip"
            suffix="$/MW-month"
            :num_years="num_years"
            :profile-type="schema.ppa.properties.capacity.profileType"
            @change="$emit('change')"
          ></InputYearlyProfile>

          <template v-if="item.type === 'THERMAL'">
            <v-switch
              v-model="item.incremental_heat_rate"
              label="Incremental Heat Rate"
              hint="Generator efficiency varies depending on load"
              persistent-hint
            ></v-switch>
            <InputProfile
              v-if="item.incremental_heat_rate"
              v-model="item.heatRateProfile"
              object-value
              label="Heat Rate"
              hint="Marginal heat rate at each power interval"
              profile-type="INCREMENTAL_HEAT_RATE"
              suffix="MMBtu/MWh"
              :use-headers="['fromPercent', 'toPercent', 'heatRate']"
              @change="$emit('change')"
            ></InputProfile>
            <InputTextField
              v-else
              v-model="item.heatRate"
              label="Heat Rate"
              tooltip="Rate at which generator turns fuel energy into electric energy"
              suffix="MMBtu/MWh"
              type="number"
              min="3.6"
              @change="$emit('change')"
            ></InputTextField>
            <InputServiceParam
              input-type="dual-constant-profile"
              v-model="item.carbonPrice"
              :label="schema.carbonPrice.title"
              :suffix="schema.carbonPrice.unit"
              :num_years="num_years"
              :profile-type="schema.carbonPrice.profileType"
              :year_start="year_start"
              spread-values
              :fill-value="0"
              @change="$emit('change')"
            ></InputServiceParam>
            <InputTextField
              v-model="item.co2Rate"
              :label="schema.co2Rate.title"
              :tooltip="schema.co2Rate.tooltip"
              :suffix="schema.co2Rate.unit"
              type="number"
              :min="schema.co2Rate.minimum"
              @change="$emit('change')"
            ></InputTextField>
            <InputServiceParam
              input-type="dual-constant-profile"
              v-model="item.fuelPrice"
              :label="schema.fuelPrice.title"
              :suffix="schema.fuelPrice.unit"
              tooltip="This parameter defines the fuel price for the unit and may be specified as either an annual constant or an hourly price profile"
              :num_years="num_years"
              :profile-type="schema.fuelPrice.profileType"
              :year_start="year_start"
              @change="$emit('change')"
            ></InputServiceParam>
            <v-expansion-panels class="pa-2">
              <v-expansion-panel>
                <v-expansion-panel-header class="text-subtitle-1 font-weight-medium"
                  >Services
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <SystemProjectServices
                    v-model="item.services"
                    :project-demands="demands"
                    :project-reserves="reserves"
                    :project-generators="generators"
                    :year_start="year_start"
                    :num_years="num_years"
                    :services-schema="thermalServicesSchema"
                    resource-type="generator"
                    @change="$emit('change')"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-container>
      </v-expand-transition>
    </template>
  </Aggregate>
</template>

<script>
import { titleCase } from "@/plugins/utils";
import { CaseModel, schemas } from "data-dictionary";

export default {
  name: "SystemProjectGenerator",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
    InputServiceParam: () => import("@/components/InputServiceParam.vue"),
    InputTextField: () => import("@/components/InputTextField.vue"),
    InputToggleParam: () => import("@/components/InputToggleParam.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
    SystemProjectServices: () => import("@/components/SystemProjectServices.vue"),
  },
  mixins: [],
  filters: { titleCase },
  props: {
    value: { type: Array, required: true },
    year_start: { type: Number, required: true },
    num_years: { type: Number, required: true },
    demands: { type: Array, required: true },
    reserves: { type: Array, required: true },
    generators: { type: Array, required: true },
    errors: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      aggregateHeaders: [
        ...CaseModel.GeneratorProject.HEADERS,
        { text: "Active", value: "active" },
      ],
      schema: {
        ...schemas.find((v) => v.$id === "generator_project.schema.json").properties,
        ...schemas
          .find((v) => v.$id === "generator_project.schema.json")
          .allOf.map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      },
      thermalServicesSchema: schemas.find((v) => v.$id === "generator_project.schema.json").allOf[1]
        .then.properties.services.properties,
      renewableSourceEnum: schemas.find((v) => v.$id === "generator_project.schema.json").allOf[0]
        .then.properties.source.enum,
      thermalSourceEnum: schemas.find((v) => v.$id === "generator_project.schema.json").allOf[1]
        .then.properties.source.enum,
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    generatorProjectFactory: CaseModel.GeneratorProject,
    changeItemType(item) {
      if (item.type === "THERMAL") {
        const thermal = new CaseModel.GeneratorProject(item);
        const valueCopy = this.value;
        const itemIndex = valueCopy.findIndex((v) => v.id === item.id);
        valueCopy.splice(itemIndex, 1, thermal);
        this.computedValue = valueCopy;
      }
      this.$emit("change");
    },
  },
};
</script>

<style></style>
